import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  useTheme,
  Tooltip,
  Alert,
  LinearProgress,
  CircularProgress,
  Chip,
  Snackbar,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  CheckCircle,
  CalendarToday,
} from "@mui/icons-material";
import LoadingError from "./LoadingError";
import Layout from "./Layout";
import {
  formatLocalDate,
  isOverdue,
  toDateInputValue,
  toTrelloDue,
} from "../utils/trelloDateUtils";
import { useAuth } from "../utils/AuthContext";

const TrelloCards = () => {
  const theme = useTheme();

  // State variables
  const [openCards, setOpenCards] = useState([]);
  const [closedCards, setClosedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boardId, setBoardId] = useState(null);
  const [openListId, setOpenListId] = useState(null);
  const [closedListId, setClosedListId] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState("");
  const [newCardDescription, setNewCardDescription] = useState("");
  const [newCardDueDate, setNewCardDueDate] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editCardTitle, setEditCardTitle] = useState("");
  const [editCardDescription, setEditCardDescription] = useState("");
  const [editCardDueDate, setEditCardDueDate] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [processingCards, setProcessingCards] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Trello API configuration
  const openListName = "Client Open Tasks";
  const closedListName = "Client Closed Tasks";
  const apiKey = process.env.REACT_APP_TRELLO_API_KEY;
  const apiToken = process.env.REACT_APP_TRELLO_API_TOKEN;
  const apiUrl = process.env.REACT_APP_API_URL;

  const { user } = useAuth();
  const role = user?.role || null;

  // Allowed roles for editing/creating
  const allowedRoles = ["admin", "manager"];
  const canEdit = allowedRoles.includes(role);

  // -------------------------
  // Sorting
  // -------------------------
  const sortCardsByDueDate = useCallback((cards) => {
    return [...cards].sort((a, b) => {
      if (a.due && !b.due) return -1;
      if (!a.due && b.due) return 1;
      if (!a.due && !b.due) return 0;
      const ta = new Date(toDateInputValue(a.due)).getTime();
      const tb = new Date(toDateInputValue(b.due)).getTime();
      return ta - tb;
    });
  }, []);

  // -------------------------
  // Error handling
  // -------------------------
  const handleError = useCallback(
    (error, defaultMessage = "An error occurred") => {
      console.error(error);
      const errorMessage =
        error?.response?.data?.error || error?.message || defaultMessage;
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
      setLoading(false);
      setIsSubmitting(false);
    },
    []
  );

  // -------------------------
  // API calls
  // -------------------------
  useEffect(() => {
    const fetchBoardId = async () => {
      try {
        const response = await axios.get(`${apiUrl}/account/board`);
        setBoardId(response.data.board_id);
      } catch (err) {
        handleError(err, "Failed to fetch board ID");
      }
    };
    fetchBoardId();
  }, [apiUrl, handleError]);

  useEffect(() => {
    if (!boardId) return;

    const fetchLists = async () => {
      try {
        const response = await axios.get(
          `https://api.trello.com/1/boards/${boardId}/lists`,
          {
            params: { key: apiKey, token: apiToken },
          }
        );

        const openList = response.data.find(
          (list) => list.name === openListName
        );
        const closedList = response.data.find(
          (list) => list.name === closedListName
        );

        if (openList && closedList) {
          setOpenListId(openList.id);
          setClosedListId(closedList.id);
          await Promise.all([
            fetchCards(openList.id, "open"),
            fetchCards(closedList.id, "closed"),
          ]);
        } else {
          handleError(new Error("Required lists not found"));
        }
      } catch (err) {
        handleError(err, "Failed to fetch lists");
      }
    };

    fetchLists();
  }, [boardId, apiKey, apiToken, openListName, closedListName, handleError]);

  const fetchCards = useCallback(
    async (listId, listType) => {
      try {
        const response = await axios.get(
          `https://api.trello.com/1/lists/${listId}/cards`,
          {
            params: { key: apiKey, token: apiToken },
          }
        );

        const cardsWithCompletion = response.data.map((card) => ({
          ...card,
          completed: listType === "closed",
          due: card.due || null,
        }));

        if (listType === "open") {
          setOpenCards(sortCardsByDueDate(cardsWithCompletion));
        } else {
          setClosedCards(cardsWithCompletion);
        }
      } catch (err) {
        handleError(err, "Failed to fetch cards");
      } finally {
        setLoading(false);
        setError(null);
      }
    },
    [apiKey, apiToken, handleError, sortCardsByDueDate]
  );

  // -------------------------
  // Actions
  // -------------------------
  const handleCheckboxChange = useCallback(
    async (cardId, listType) => {
      setProcessingCards((prev) => ({ ...prev, [cardId]: true }));
      try {
        const targetListId = listType === "open" ? closedListId : openListId;
        if (!targetListId) throw new Error("Target list not found");

        await axios.put(`https://api.trello.com/1/cards/${cardId}`, null, {
          params: { key: apiKey, token: apiToken, idList: targetListId },
        });

        if (listType === "open") {
          setOpenCards((prev) => prev.filter((card) => card.id !== cardId));
          fetchCards(closedListId, "closed");
        } else {
          setClosedCards((prev) => prev.filter((card) => card.id !== cardId));
          fetchCards(openListId, "open");
        }
      } catch (err) {
        handleError(err, "Failed to move task");
      } finally {
        setProcessingCards((prev) => {
          const n = { ...prev };
          delete n[cardId];
          return n;
        });
      }
    },
    [openListId, closedListId, apiKey, apiToken, fetchCards, handleError]
  );

  const handleCreateCard = async () => {
    setIsSubmitting(true);
    try {
      if (!openListId) throw new Error("Open list not found");
      if (!newCardTitle.trim()) {
        setSnackbar({
          open: true,
          message: "Task title is required",
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      const formattedDueDate = newCardDueDate
        ? toTrelloDue(newCardDueDate)
        : null;
      const params = {
        key: apiKey,
        token: apiToken,
        idList: openListId,
        name: newCardTitle,
        desc: newCardDescription,
      };
      if (formattedDueDate) params.due = formattedDueDate;

      const response = await axios.post(
        `https://api.trello.com/1/cards`,
        null,
        { params }
      );

      const newCard = {
        ...response.data,
        due: response.data.due ?? formattedDueDate ?? null,
        completed: false,
      };
      setOpenCards((prev) => sortCardsByDueDate([...prev, newCard]));
      handleCreateDialogClose();
      setSnackbar({
        open: true,
        message: "Task created successfully!",
        severity: "success",
      });
    } catch (err) {
      handleError(err, "Failed to create task");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCard = useCallback(
    async (cardId, listType) => {
      setProcessingCards((prev) => ({ ...prev, [cardId]: true }));
      try {
        await axios.delete(`https://api.trello.com/1/cards/${cardId}`, {
          params: { key: apiKey, token: apiToken },
        });
        if (listType === "open")
          setOpenCards((prev) => prev.filter((card) => card.id !== cardId));
        else
          setClosedCards((prev) => prev.filter((card) => card.id !== cardId));
        setSnackbar({
          open: true,
          message: "Task deleted successfully!",
          severity: "success",
        });
      } catch (err) {
        handleError(err, "Failed to delete task");
      } finally {
        setProcessingCards((prev) => {
          const n = { ...prev };
          delete n[cardId];
          return n;
        });
      }
    },
    [apiKey, apiToken, handleError]
  );

  const handleEditCard = async () => {
    setIsSubmitting(true);
    try {
      if (!editCardTitle.trim()) {
        setSnackbar({
          open: true,
          message: "Task title is required",
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      const formattedDueDate = editCardDueDate
        ? toTrelloDue(editCardDueDate)
        : null;
      const params = {
        key: apiKey,
        token: apiToken,
        name: editCardTitle,
        desc: editCardDescription,
      };
      if (formattedDueDate) params.due = formattedDueDate;
      else params.due = null;

      await axios.put(
        `https://api.trello.com/1/cards/${editingCard.id}`,
        null,
        { params }
      );

      const updateCards = (prev) =>
        prev.map((card) =>
          card.id === editingCard.id
            ? {
                ...card,
                name: editCardTitle,
                desc: editCardDescription,
                due: formattedDueDate,
              }
            : card
        );
      if (openCards.some((card) => card.id === editingCard.id))
        setOpenCards((prev) => sortCardsByDueDate(updateCards(prev)));
      else setClosedCards((prev) => updateCards(prev));

      handleEditDialogClose();
      setSnackbar({
        open: true,
        message: "Task updated successfully!",
        severity: "success",
      });
    } catch (err) {
      handleError(err, "Failed to update task");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dialog handlers
  const handleCreateDialogOpen = () => setIsCreateDialogOpen(true);
  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
    setNewCardTitle("");
    setNewCardDescription("");
    setNewCardDueDate("");
  };
  const handleEditDialogOpen = (card) => {
    setEditingCard(card);
    setEditCardTitle(card.name);
    setEditCardDescription(card.desc);
    setEditCardDueDate(card.due ? toDateInputValue(card.due) : "");
    setIsEditDialogOpen(true);
  };
  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setEditingCard(null);
    setEditCardTitle("");
    setEditCardDescription("");
    setEditCardDueDate("");
  };

  // TaskCard component
  const TaskCard = ({ card, listType }) => {
    const isProcessing = processingCards[card.id];
    const isCompleted = listType === "closed";
    const dueDateFormatted = card.due ? formatLocalDate(card.due) : "";
    const isTaskOverdue = isOverdue(card.due) && !isCompleted;

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${
            isCompleted
              ? theme.palette.success.main
              : theme.palette.primary.main
          }`,
          opacity: isProcessing ? 0.7 : 1,
          position: "relative",
          overflow: "hidden",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        {isProcessing && (
          <LinearProgress
            color={isCompleted ? "success" : "primary"}
            sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
          />
        )}
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.text.primary,
              textDecoration: isCompleted ? "line-through" : "none",
              opacity: isCompleted ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isCompleted && <CheckCircle fontSize="small" color="success" />}
            {card.name}
          </Typography>
          {card.due && (
            <Box
              sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              <CalendarToday
                fontSize="small"
                sx={{
                  color: isTaskOverdue
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isTaskOverdue
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                  fontWeight: isTaskOverdue ? 600 : "normal",
                }}
              >
                {dueDateFormatted}
                {isTaskOverdue && " (Overdue)"}
              </Typography>
              {isTaskOverdue && !isCompleted && (
                <Chip
                  label="Overdue"
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.contrastText,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: theme.palette.text.secondary,
              minHeight: "4em",
              opacity: isCompleted ? 0.7 : 1,
            }}
          >
            {card.desc || "No description provided"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompleted}
                  onChange={() => handleCheckboxChange(card.id, listType)}
                  color={isCompleted ? "success" : "primary"}
                  disabled={isProcessing}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                />
              }
              label={isCompleted ? "Completed" : "Mark Complete"}
              sx={{ "& .MuiFormControlLabel-label": { fontWeight: 500 } }}
            />
            {canEdit && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Edit Task">
                  <IconButton
                    onClick={() => handleEditDialogOpen(card)}
                    disabled={isProcessing}
                    sx={{ color: theme.palette.primary.main }}
                    aria-label="edit task"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Task">
                  <IconButton
                    onClick={() => handleDeleteCard(card.id, listType)}
                    disabled={isProcessing}
                    sx={{ color: theme.palette.error.main }}
                    aria-label="delete task"
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const EmptyState = ({ message }) => (
    <Box
      sx={{ textAlign: "center", py: 6, color: theme.palette.text.secondary }}
    >
      <CheckCircle sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
      <Typography variant="h6">{message}</Typography>
    </Box>
  );

  return (
    <Layout role={null}>
      {loading || error ? (
        <LoadingError isLoading={loading} error={error} />
      ) : (
        <Box
          sx={{
            p: 4,
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Tasks
            </Typography>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateDialogOpen}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 500,
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-1px)",
                  },
                }}
                aria-label="create new task"
              >
                New Task
              </Button>
            )}
          </Box>

          {/* Task Columns */}
          <Grid container spacing={3} sx={{ width: "100%", flexGrow: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Open Tasks
              </Typography>
              {openCards.length > 0 ? (
                openCards.map((card) => (
                  <TaskCard key={card.id} card={card} listType="open" />
                ))
              ) : (
                <EmptyState message="No open tasks" />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Closed Tasks
              </Typography>
              {closedCards.length > 0 ? (
                closedCards.map((card) => (
                  <TaskCard key={card.id} card={card} listType="closed" />
                ))
              ) : (
                <EmptyState message="No closed tasks" />
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Create Task Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCreateDialogClose}>
        <DialogTitle>Create Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={newCardTitle}
            onChange={(e) => setNewCardTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={newCardDescription}
            onChange={(e) => setNewCardDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Due Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newCardDueDate}
            onChange={(e) => setNewCardDueDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          <Button
            onClick={handleCreateCard}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleEditDialogClose}>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={editCardTitle}
            onChange={(e) => setEditCardTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={editCardDescription}
            onChange={(e) => setEditCardDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Due Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={editCardDueDate}
            onChange={(e) => setEditCardDueDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>Cancel</Button>
          <Button
            onClick={handleEditCard}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TrelloCards;
